// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="FilterBar" set="mytaible-shadcn-studio" verified=tbd

import { ChevronDown, ChevronLeft, ChevronUp, ListFilter, X } from 'lucide-react';
import * as React from 'react';
import type { SortState } from '../../hooks/use-client-sort';
import { cn } from '../../lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FilterBarDef {
  /** Unique key identifying this filter attribute */
  key: string;
  /** Display label shown in chips and the picker */
  label: string;
  /** 'select' shows a searchable option list; 'text' accepts free-form input */
  type: 'select' | 'text';
  options?: { value: string; label: string; icon?: React.ReactNode }[];
  /** Optional icon shown in the attribute list */
  icon?: React.ReactNode;
}

export interface FilterBarValue {
  key: string;
  value: string;
  /** Human-readable label displayed in the chip */
  label: string;
}

export interface FilterBarSortField {
  field: string;
  label: string;
}

export interface FilterBarProps {
  defs: FilterBarDef[];
  value: FilterBarValue[];
  onChange: (value: FilterBarValue[]) => void;
  className?: string;
  /**
   * 'inline' (default) — trigger, chips, and clear all on one row.
   * 'split' — trigger + clear on the first row (alongside `leading`), chips on a second row.
   */
  layout?: 'inline' | 'split';
  /** Only used with layout="split". Rendered left of the filter controls on the first row. */
  leading?: React.ReactNode;
  /** Sort field definitions. When provided, a Sort section appears inside the filter popover. */
  sortFields?: FilterBarSortField[];
  /** Current sort state. Required when sortFields is provided. */
  sort?: SortState;
  /** Called when the user selects a sort field or toggles direction. */
  onSort?: (field: string) => void;
  /** Suppress the built-in "Clear all" button. Use when the caller renders its own clear control. */
  hideClearAll?: boolean;
  /** Rendered as a footer inside the filter popover (attribute step only). Receives a close() callback. */
  actions?: (close: () => void) => React.ReactNode;
}

// ─── FilterBar ────────────────────────────────────────────────────────────────

const FilterBar = React.forwardRef<HTMLDivElement, FilterBarProps>(
  (
    {
      defs,
      value,
      onChange,
      className,
      layout = 'inline',
      leading,
      sortFields,
      sort,
      onSort,
      hideClearAll,
      actions,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [step, setStep] = React.useState<'attribute' | 'value'>('attribute');
    const [pendingDef, setPendingDef] = React.useState<FilterBarDef | null>(null);
    const [search, setSearch] = React.useState('');

    const appliedKeys = React.useMemo(() => new Set(value.map((f) => f.key)), [value]);
    const availableDefs = React.useMemo(
      () => defs.filter((d) => !appliedKeys.has(d.key)),
      [defs, appliedKeys],
    );
    const defByKey = React.useMemo(() => Object.fromEntries(defs.map((d) => [d.key, d])), [defs]);

    const isTextStep = step === 'value' && pendingDef?.type === 'text';

    function handleOpenChange(o: boolean) {
      setOpen(o);
      if (!o) resetPicker();
    }

    function resetPicker() {
      setStep('attribute');
      setPendingDef(null);
      setSearch('');
    }

    function selectAttribute(def: FilterBarDef) {
      setPendingDef(def);
      setSearch('');
      setStep('value');
    }

    function applyFilter(filterValue: string, filterLabel: string) {
      if (!pendingDef) return;
      onChange([...value, { key: pendingDef.key, value: filterValue, label: filterLabel }]);
      resetPicker();
      setOpen(false);
    }

    function removeFilter(key: string) {
      onChange(value.filter((f) => f.key !== key));
    }

    const picker = availableDefs.length > 0 && (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={value.length === 0 ? 'Filter' : 'Add filter'}
            className="flex size-8 items-center justify-center rounded-full bg-card/90 text-foreground shadow-sm ring-1 ring-border/60 transition-colors hover:bg-card"
          >
            <ListFilter className="size-4" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          className="w-64 overflow-hidden rounded-2xl border-0 bg-popover text-popover-foreground p-0 shadow-2xl"
          align="start"
        >
          <Command shouldFilter={!isTextStep} className="bg-transparent">
            {step === 'value' && pendingDef && (
              <div className="flex items-center gap-2 border-b border-border/40 px-3 py-2">
                <button
                  type="button"
                  onClick={() => {
                    setStep('attribute');
                    setSearch('');
                  }}
                  className="flex size-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Back to attributes"
                >
                  <ChevronLeft className="size-5" />
                </button>
                <span className="text-[15px] font-medium">{pendingDef.label}</span>
              </div>
            )}

            <CommandInput
              placeholder={
                step === 'attribute'
                  ? 'Filter by…'
                  : isTextStep
                    ? 'Type to search…'
                    : `Search ${pendingDef?.label}…`
              }
              value={search}
              onValueChange={setSearch}
              onKeyDown={(e) => {
                if (isTextStep && e.key === 'Enter' && search.trim()) {
                  e.preventDefault();
                  applyFilter(search.trim(), search.trim());
                }
                if (step === 'value' && e.key === 'Backspace' && !search) {
                  e.preventDefault();
                  setStep('attribute');
                  setSearch('');
                }
              }}
            />

            <CommandList>
              {step === 'attribute' && sortFields && sortFields.length > 0 && sort && onSort && (
                <>
                  <CommandGroup heading="Sort by">
                    {sortFields.map(({ field, label }) => {
                      const active = sort.field === field;
                      return (
                        <CommandItem
                          key={field}
                          value={`sort:${field}`}
                          onSelect={() => {
                            onSort(field);
                            setOpen(false);
                          }}
                          className="flex items-center justify-between"
                        >
                          <span>{label}</span>
                          {active &&
                            (sort.dir === 'asc' ? (
                              <ChevronUp className="size-3.5 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="size-3.5 text-muted-foreground" />
                            ))}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                  {availableDefs.length > 0 && <CommandSeparator />}
                </>
              )}

              {step === 'attribute' && (
                <>
                  <CommandEmpty>No filters found.</CommandEmpty>
                  <CommandGroup>
                    {availableDefs.map((def) => (
                      <CommandItem
                        key={def.key}
                        value={def.label}
                        onSelect={() => selectAttribute(def)}
                      >
                        {def.icon}
                        {def.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              {step === 'value' && pendingDef?.type === 'select' && (
                <>
                  <CommandEmpty>No options found.</CommandEmpty>
                  <CommandGroup>
                    {pendingDef.options?.map((opt) => (
                      <CommandItem
                        key={opt.value}
                        value={opt.label}
                        onSelect={() => applyFilter(opt.value, opt.label)}
                      >
                        {opt.icon}
                        {opt.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              {isTextStep &&
                (search.trim() ? (
                  <CommandGroup>
                    <CommandItem
                      value={search}
                      onSelect={() => applyFilter(search.trim(), search.trim())}
                    >
                      Apply &ldquo;{search.trim()}&rdquo;
                    </CommandItem>
                  </CommandGroup>
                ) : (
                  <p className="py-10 text-center text-[15px] text-muted-foreground">
                    Type to add a filter…
                  </p>
                ))}
            </CommandList>
            {step === 'attribute' && actions && (
              <div className="border-t border-border/40 p-2">{actions(() => setOpen(false))}</div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    );

    const chips = value.map((f) => {
      const def = defByKey[f.key];
      return (
        <div
          key={f.key}
          className="flex h-7 items-center gap-1.5 rounded-full bg-card/90 px-3 text-xs font-medium shadow-sm ring-1 ring-border/60"
        >
          <span className="text-muted-foreground">{def?.label ?? f.key}:</span>
          <span>{f.label}</span>
          <button
            type="button"
            onClick={() => removeFilter(f.key)}
            aria-label={`Remove ${def?.label ?? f.key} filter`}
            className="ml-0.5 rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="size-3" />
          </button>
        </div>
      );
    });

    const clearAll = !hideClearAll && value.length > 0 && (
      <button
        type="button"
        onClick={() => onChange([])}
        className="flex h-7 items-center rounded-full bg-card/90 px-3 text-xs font-medium text-muted-foreground shadow-sm ring-1 ring-border/60 transition-colors hover:bg-card hover:text-foreground"
      >
        Clear all
      </button>
    );

    if (layout === 'split') {
      return (
        <div ref={ref} className={cn('space-y-2', className)}>
          <div className="flex items-center gap-2">
            {leading && <div className="flex-1">{leading}</div>}
            {picker}
            {clearAll}
          </div>
          {chips.length > 0 && <div className="flex flex-wrap gap-2">{chips}</div>}
        </div>
      );
    }

    return (
      <div ref={ref} className={cn('flex flex-wrap items-center gap-2', className)}>
        {picker}
        {chips}
        {clearAll}
      </div>
    );
  },
);
FilterBar.displayName = 'FilterBar';

export { FilterBar };
